import React, {
    KeyboardEvent,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import {
    Descendant,
    Editor,
    Element,
    Node,
    Path,
    Range,
    Text,
    Transforms,
} from 'slate';
import { Editable, ReactEditor, RenderLeafProps, useSlate } from 'slate-react';
import { v4 as uuid } from 'uuid';

// other components
import BasicButton from '../../../components/buttons/basicButton';
// editor specific components
import ButtonElementMain from '../../../editors/PushNotificationEditor/Editor/components/ButtonElementMain';
import CMSPlaceholderElementComponent from '../../../editors/PushNotificationEditor/Editor/components/CMSPlaceholderElement';
import CreateComponentsDropdown from '../../../editors/PushNotificationEditor/Editor/components/CreateComponentsDropdown';
import DatasetElement from '../../../editors/PushNotificationEditor/Editor/components/DatasetElement';
import DeeplinkComponentV2 from '../../../editors/PushNotificationEditor/Editor/components/Deeplink/DeeplinkComponentV2';
import ImageInputElement from '../../../editors/PushNotificationEditor/Editor/components/Image/ImageInputElement';
import Leaf from '../../../editors/PushNotificationEditor/Editor/components/Leaf';
import LinkElementComponent from '../../../editors/PushNotificationEditor/Editor/components/LinkElementComponent';
import AttributePlaceholderElementComponent from '../../../editors/PushNotificationEditor/Editor/components/SlateInput/AttributePlaceholder/AttributePlaceholderElementComponent';
import SlateInputElement from '../../../editors/PushNotificationEditor/Editor/components/SlateInput/SlateInputElement';
import SurfaceButtonElement from '../../../editors/PushNotificationEditor/Editor/components/SurfaceButtonElement';
import VariantElement from '../../../editors/PushNotificationEditor/Editor/components/VariantElement';
import FormulaEditorToolbar, {
    FormulaEditorToolbarControls,
} from './assistant/components/FormulaEditorToolbar';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
// constants
import ICONS from '../../../constants/icons';

// context
import { EditorContext } from '../../../context/EditorContext';

// helpers
import {
    getEmailFieldData,
    showImageAlternates,
} from '../../../helpers/formulaHelpers';

// hooks
import useQueryEmailTemplate from '../../../hooks/EmailTemplate/useQueryEmailTemplate';
import useAnalytics from '../../../hooks/helper/useAnalytics';

//models
import {
    ButtonType,
    CMSPlaceholderElement,
    DatasetElement as DatasetElementModel,
    ElementKind,
    SlateElementsData,
    AttributePlaceholderElement,
    getSlateInputType,
} from '../../../models/editor.model';
import {
    FormulaStatus,
    FormulaTypesData,
    MaxCharacterModel,
} from '../../../models/formula.model';
import { SurfaceButtonFieldLabel } from '../../../models/surface.model';

import { FormulaCreateType } from '../FormulaCreateMainV2';

interface PnEditorProps {
    formulaId?: number;
    setSelectedElement: (value: { [key: string]: any }) => void;
    selectedElement?: { [key: string]: any };
    currentSelection?: Range;
    handleAddButton: () => void;
    handleRemoveButton: (buttonType: ButtonType) => void;
    formulaHasSecondaryButton: boolean;
    createFormula: FormulaCreateType;
    maxCharacterCounts?: MaxCharacterModel;
    editCMSPlaceholder: (id: string, cmsPlaceholderId: string) => void;
    editUserAttributePlaceholder: (
        id: string,
        defaultValue: string,
        attributeName: string,
    ) => void;
    setAlternateCreateError: (value: string | null) => void;
    handleUpdateStructure: (newValue: Descendant[]) => void;
    status?: FormulaStatus;
    editDataset?: (id: string, edits: any) => void;
    toolbarControls?: FormulaEditorToolbarControls[];
    hasWriteAccess: boolean;
}

const PnEditor: React.FC<React.PropsWithChildren<PnEditorProps>> = ({
    formulaId,
    setSelectedElement,
    selectedElement,
    currentSelection,
    handleAddButton,
    handleRemoveButton,
    formulaHasSecondaryButton,
    createFormula,
    maxCharacterCounts,
    editCMSPlaceholder,
    setAlternateCreateError,
    handleUpdateStructure,
    status,
    editDataset,
    toolbarControls,
    editUserAttributePlaceholder,
    hasWriteAccess,
}) => {
    const editor = useSlate();
    const { track } = useAnalytics();
    const {
        editorDetails: { formulaType, formula },
    } = useContext(EditorContext);

    const [editingVariant, setEditingVariant] = useState<boolean>();
    const [isLinkModalOpen, setIsLinkModalOpen] = useState<boolean>(false);
    const { data: templateData } = useQueryEmailTemplate(
        formula?.additional_metadata?.email_template_id as string,
        formulaType as FormulaType,
        !formula?.additional_metadata?.is_aampe_custom_template,
    );

    const fieldsWithoutModifiers = useMemo<string[]>(
        () => templateData?.dynamicFieldsWithoutModifiers ?? [],
        [templateData],
    );

    const isDisabled = useMemo(() => {
        if (!hasWriteAccess) {
            return true;
        }
        if (currentSelection) {
            const hasNode: boolean = Node.has(
                editor,
                currentSelection.anchor.path,
            );
            if (hasNode) {
                const node: any = Node.get(
                    editor,
                    currentSelection.anchor.path,
                );
                return node.kind === ElementKind.variant;
            }
        }
        return false;
    }, [currentSelection, editor, hasWriteAccess]);

    const allowAddButton = useMemo<boolean>(
        () =>
            !!(
                formulaType &&
                FormulaTypesData[formulaType].allowButtons &&
                !formulaHasSecondaryButton
            ),
        [formulaType, formulaHasSecondaryButton],
    );

    // EDITOR COMMANDS
    const insertDataset = useCallback(
        (datasetFieldId: number) => {
            let newElement: DatasetElementModel = {
                kind: ElementKind.dataset,
                id: uuid(),
                children: [{ text: '' }],
                datasetField: datasetFieldId,
                datasetId: formula?.dataset_id!,
            };

            Transforms.insertNodes(editor, newElement, {
                at: editor.selection?.focus,
            });

            //delay selecting the node so that it can be fully initialized
            setTimeout(() => {
                const path = ReactEditor.findPath(editor, newElement);
                // Add a 0 to the path to select the first child of the dataset
                // which is a text node, If we don't do this, Slate throws an error
                // saying that it can't select a non-leaf node.
                path.push(0);
                const newLocation = {
                    anchor: { path: path as Path, offset: 0 },
                    focus: { path: path as Path, offset: 0 },
                };
                Transforms.select(editor, newLocation);
            }, 200);

            track('formula_dataset_inserted');
        },
        [editor, formula, track],
    );

    const insertCMSPlaceholder = useCallback(
        (id: string) => {
            const newElement: CMSPlaceholderElement = {
                kind: ElementKind.cms_placeholder,
                id: uuid(),
                children: [{ text: '' }],
                cmsPlaceholderId: id,
            };
            Transforms.insertNodes(editor, newElement, {
                at: editor.selection?.focus,
            });
            track('formula_cms_placeholder_inserted');
        },
        [editor, track],
    );

    const insertUserAttributePlaceholder = useCallback(
        (defaultValue: string, userAttribute: string) => {
            const newElement: AttributePlaceholderElement = {
                kind: ElementKind.attributePlaceholder,
                id: uuid(),
                defaultValue: defaultValue,
                attributeName: userAttribute,
                children: [{ text: '' }],
            };
            Transforms.insertNodes(editor, newElement as any, {
                at: editor.selection?.focus,
            });
            track('formula_attribute_placeholder_inserted', {
                formula_id: formulaId,
            });
        },
        [editor, formulaId, track],
    );

    const insertEmoji = useCallback(
        (emoji: string) => {
            Transforms.insertText(editor, emoji, {
                at: editor.selection?.focus,
            });
            Transforms.move(editor);
        },
        [editor],
    );

    const checkAndRenderImageAlternates = useCallback<
        (element: any) => boolean
    >(
        (element: any) =>
            formulaType ? showImageAlternates(formulaType, element) : false,
        [formulaType],
    );

    // RENDER FUNCTIONS //
    const renderElement = useCallback(
        (props: any) => {
            props.formulaId = formulaId;
            const surfaceFieldLabel =
                props.element?.surfaceFieldLabel ??
                (props.element?.surfaceFieldName ===
                SurfaceButtonFieldLabel.Text
                    ? 'text'
                    : props.element?.surfaceFieldName);

            switch (props.element.kind) {
                case ElementKind.section:
                case ElementKind.buttonAction:
                case ElementKind.buttonText:
                case ElementKind.image:
                    if (checkAndRenderImageAlternates(props.element)) {
                        return (
                            <ImageInputElement
                                selectedElement={selectedElement}
                                handleUpdateStructure={handleUpdateStructure}
                                status={status}
                                isDisabled={!hasWriteAccess}
                                label={surfaceFieldLabel}
                                {...props}
                            />
                        );
                    }
                    return (
                        <SlateInputElement
                            isDisabled={isDisabled}
                            readOnly={!hasWriteAccess}
                            insertEmoji={insertEmoji}
                            insertCMSPlaceholder={insertCMSPlaceholder}
                            insertDataset={insertDataset}
                            selectedElement={selectedElement}
                            maxCharacterCounts={maxCharacterCounts}
                            className={'slate-field'}
                            label={surfaceFieldLabel}
                            insertUserAttributePlaceholder={
                                insertUserAttributePlaceholder
                            }
                            {...props}
                        />
                    );
                case ElementKind.variant:
                    return (
                        <VariantElement
                            {...props}
                            selectedElement={selectedElement}
                            setEditingVariant={setEditingVariant}
                            setAlternateCreateError={setAlternateCreateError}
                            className={'slate-field'}
                        />
                    );
                case ElementKind.dataset:
                    return (
                        <DatasetElement
                            {...props}
                            selectedElement={selectedElement}
                            editDataset={editDataset}
                        />
                    );
                case ElementKind.cms_placeholder:
                    return (
                        <CMSPlaceholderElementComponent
                            {...props}
                            selectedElement={selectedElement}
                            editCMSPlaceholder={editCMSPlaceholder}
                        />
                    );
                case ElementKind.attributePlaceholder:
                    return (
                        <AttributePlaceholderElementComponent
                            {...props}
                            editUserAttributePlaceholder={
                                editUserAttributePlaceholder
                            }
                        />
                    );
                case ElementKind.button:
                    return (
                        <ButtonElementMain
                            {...props}
                            handleRemoveButton={handleRemoveButton}
                            formulaHasSecondaryButton={
                                formulaHasSecondaryButton
                            }
                            label={props.element?.surfaceFieldName}
                        />
                    );
                case ElementKind.link:
                    return (
                        <LinkElementComponent
                            {...props}
                            selectedElement={selectedElement}
                            isLinkModalOpen={isLinkModalOpen}
                            setIsLinkModalOpen={setIsLinkModalOpen}
                        />
                    );

                case ElementKind.surfaceButton:
                    return (
                        <SurfaceButtonElement
                            label={props.element?.surfaceFieldLabel}
                            {...props}
                        />
                    );
                default:
                    return (
                        <div {...props.attributes}>
                            <span contentEditable={false}>ERROR</span>
                            {props.children}
                        </div>
                    );
            }
        },
        [
            formulaId,
            checkAndRenderImageAlternates,
            isDisabled,
            insertEmoji,
            insertCMSPlaceholder,
            insertDataset,
            selectedElement,
            maxCharacterCounts,
            setAlternateCreateError,
            editDataset,
            editCMSPlaceholder,
            handleRemoveButton,
            formulaHasSecondaryButton,
            isLinkModalOpen,
            handleUpdateStructure,
            status,
            insertUserAttributePlaceholder,
            editUserAttributePlaceholder,
            hasWriteAccess,
        ],
    );

    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        [],
    );

    const selectElement = useCallback(
        (event: KeyboardEvent, updatedPath: any, offset: number) => {
            const node = Node.get(editor, updatedPath);
            if (
                !Editor.isEditor(node) &&
                !Text.isText(node) &&
                node.kind === ElementKind.variant
            ) {
                const { kind, variantType, id } = node;
                event.preventDefault();
                setSelectedElement({
                    kind,
                    variantType,
                    elementId: id,
                });
                Transforms.select(editor, {
                    offset: offset,
                    path: updatedPath,
                });
            }
        },
        [editor, setSelectedElement],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            // check if user tries to select all with cmd-a or ctrl-a.  The default behavior was to select all panels at once
            switch (event.key) {
                case 'a':
                    if (event.ctrlKey || event.metaKey) {
                        event.preventDefault();
                        try {
                            const {
                                selection: {
                                    anchor: { path },
                                },
                            }: any = editor;
                            const pathSelection = path[0];
                            //select all from the currently focused panel.
                            Transforms.select(editor, {
                                anchor: Editor.start(editor, [pathSelection]),
                                focus: Editor.end(editor, [pathSelection]),
                            });
                        } catch {
                            break;
                        }
                    }
                    break;
                case 'Enter': {
                    event.preventDefault();
                    const [singleLineElement] = Editor.nodes(editor, {
                        match: (node) =>
                            !Editor.isEditor(node) &&
                            !Text.isText(node) &&
                            !SlateElementsData[getSlateInputType(node)]
                                ?.isMultiLine &&
                            'sectionType' in node &&
                            !getEmailFieldData(node?.sectionType)?.isMultiLine,
                    });
                    if (!singleLineElement) {
                        editor.insertText('\n');
                    }
                    break;
                }
                case 'Backspace':
                case 'Delete':
                    try {
                        const {
                            selection: { anchor },
                        }: any = editor;

                        const [surfaceButtonEntry] = Editor.nodes(editor, {
                            match: (n: any) =>
                                n.kind === ElementKind.surfaceButton,
                        });

                        const { path, offset } = anchor;
                        const currentNode = Node.get(editor, path);
                        const updatedPath = [...path];
                        if (selectedElement?.kind === ElementKind.variant) {
                            const parentNode: any = Node.parent(
                                editor,
                                updatedPath,
                            );
                            if (parentNode?.kind === ElementKind.variant) {
                                updatedPath.pop();
                                Transforms.select(editor, {
                                    offset: offset,
                                    path: updatedPath,
                                });
                            }
                            event.preventDefault();
                        } else if (surfaceButtonEntry) {
                            const [, surfaceButtonPath]: any =
                                surfaceButtonEntry;
                            if (
                                Editor.isStart(
                                    editor,
                                    anchor,
                                    surfaceButtonPath,
                                )
                            ) {
                                event.preventDefault();
                            }
                        } else if (!Element.isElement(currentNode)) {
                            if (event.key === 'Backspace' && offset === 0) {
                                // When there is no node in the updated path, Slate throws error,
                                // hence try catch is added to it
                                updatedPath[1] -= 1;

                                // When user clicks backspace and the previous element is a variant,
                                // the cursor is moved inside the selection instead of deleting it
                                const node = Node.get(editor, updatedPath);

                                if (
                                    !Editor.isEditor(node) &&
                                    !Text.isText(node) &&
                                    node.kind === ElementKind.variant
                                ) {
                                    selectElement(
                                        event,
                                        updatedPath,
                                        node.children[0].text.length,
                                    );
                                }
                            } else if (
                                event.key === 'Delete' &&
                                Text.isText(currentNode) &&
                                offset === (currentNode.text as string).length
                            ) {
                                updatedPath[1] += 1;
                                // When user clicks delete and the next element is a variant,
                                // the cursor is moved inside the selection instead of deleting it
                                selectElement(event, updatedPath, 0);
                            }
                        }
                    } catch {
                        break;
                    }
                    break;
                default:
                    break;
            }
        },
        [editor, selectedElement, selectElement],
    );

    const handleCopyText = () => {
        const { selection } = editor;
        if (selection) {
            navigator.clipboard.writeText(Editor.string(editor, selection));
        }
    };

    const handleCutText = () => {
        handleCopyText();
    };

    return (
        <Container>
            <div style={{ borderRadius: aampeTheme.borderRadius.default }}>
                <Content className={'custom-scroll-bar'}>
                    <CreateComponentsDropdown
                        formulaId={formulaId}
                        createFormula={createFormula}
                        formulaType={formulaType as FormulaType}
                        isLinkModalOpen={isLinkModalOpen}
                        setIsLinkModalOpen={setIsLinkModalOpen}
                        selectedElement={selectedElement}
                    />

                    <Editable
                        style={{
                            borderRadius: '0 0 4px 0',
                            backgroundColor: aampeTheme.colors.backgroundWG02,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: aampeTheme.spacing(2),
                            outline: 'none',
                        }}
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        onKeyDown={onKeyDown}
                        readOnly={editingVariant || !hasWriteAccess}
                        onCopy={handleCopyText}
                        onCut={handleCutText}
                    />
                    {allowAddButton && (
                        <BasicButton
                            isPrimary={false}
                            name={'add a button'}
                            icon={<ICONS.plus size={16} />}
                            isOutlined
                            action={handleAddButton}
                            style={{ marginTop: aampeTheme.spacing(2) }}
                        />
                    )}

                    {formula && (
                        <DeeplinkComponentV2 isDisabled={!hasWriteAccess} />
                    )}
                </Content>

                <FormulaEditorToolbar
                    handleUpdateStructure={handleUpdateStructure}
                    selectedElement={selectedElement}
                    formulaId={formulaId}
                    currentSelection={currentSelection}
                    toolbarControls={toolbarControls}
                    isEditable={hasWriteAccess}
                    hasWriteAccess={hasWriteAccess}
                />

                {fieldsWithoutModifiers.length > 0 && (
                    <div>
                        <StyledFieldListTitle>
                            These Dynamic fields do not follow the Aampe naming
                            conventions.
                        </StyledFieldListTitle>
                        <ListContainer>
                            {fieldsWithoutModifiers.map((field, index) => (
                                <StyledList key={index}>{field}</StyledList>
                            ))}
                        </ListContainer>
                    </div>
                )}
            </div>
        </Container>
    );
};

const Container = styled.div(() => ({
    width: '50%',
    minHeight: '400px',
}));

const StyledFieldListTitle = styled.div({
    fontWeight: 600,
    marginBottom: aampeTheme.spacing(2),
});

const ListContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});

const StyledList = styled.div({
    marginBottom: aampeTheme.spacing(0.5),
    paddingLeft: aampeTheme.spacing(3),
});

const Content = styled.div({
    maxHeight: '660px',
    overflowY: 'auto',
    backgroundColor: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(6),
});

export default PnEditor;
